import { useState } from 'react';
import type { BoardSnapshot, BoardTeam } from '@nerds-at-play/contracts';
import { handPrompt, parseHands } from './gm-hand-assistant.js';
export type HandFields = Record<
  'resources' | 'capabilities' | 'liabilities' | 'privateInfo' | 'rationale',
  string
>;
const labels: Record<keyof HandFields, string> = {
  resources: 'Resources (JSON)',
  capabilities: 'Capabilities · one per line',
  liabilities: 'Liabilities · one per line',
  privateInfo: 'PRIVATE intelligence',
  rationale: 'GM assignment rationale',
};
export function GmHandExchange({
  board,
  onSave,
}: {
  board: BoardSnapshot;
  onSave: (team: BoardTeam, fields: HandFields) => Promise<BoardTeam>;
}) {
  const [initial] = useState(board);
  const [teams, setTeams] = useState(() =>
    board.teams.filter((t) => t.assignment?.status !== 'REVEALED'),
  );
  const [prompt, setPrompt] = useState(() => handPrompt(board));
  const [response, setResponse] = useState('');
  const [fields, setFields] = useState<Record<string, HandFields>>(() =>
    Object.fromEntries(
      teams.map((t) => [
        t.teamId,
        {
          resources: JSON.stringify(
            t.assignment?.assignment.resources ?? { money: 6, influence: 5, personnel: 3 },
            null,
            2,
          ),
          capabilities: t.assignment?.assignment.capabilities.join('\n') ?? '',
          liabilities: t.assignment?.assignment.liabilities.join('\n') ?? '',
          privateInfo: t.privateInfo ?? '',
          rationale: t.assignment?.moderatorAdjustmentReason ?? 'GM prepared the playtest hand',
        },
      ]),
    ),
  );
  const [saved, setSaved] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(teams.map((t) => [t.teamId, t.assignment?.status === 'PREPARED'])),
  );
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  return (
    <section>
      <p>
        Prepare all teams together: copy this prompt into your GM chatbot, paste its JSON response,
        then fill and review each hand. Saving keeps hands private; reveal all hands together from
        the board afterward.
      </p>
      {teams.length > 0 && teams.every((t) => saved[t.teamId]) && (
        <p role="status">
          All hands in this editor are saved privately. Next: close this editor and choose Reveal
          all hands under Game Master · next decision. Teams must acknowledge their revealed hands
          before you can open Round 1.
        </p>
      )}
      <label>
        GM prompt for all organizations
        <textarea rows={10} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
      </label>
      <button
        disabled={busy}
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(prompt);
              setError('');
              setStatus('GM prompt copied.');
            } catch {
              setError('Copy unavailable. Select the prompt and copy it manually.');
            }
          })();
        }}
      >
        Copy GM prompt
      </button>
      <label>
        Paste all-team chatbot response
        <textarea rows={10} value={response} onChange={(e) => setResponse(e.target.value)} />
      </label>
      <button
        disabled={busy}
        onClick={() => {
          try {
            const hands = parseHands(
              response,
              teams.map((t) => t.teamId),
            );
            setFields(
              Object.fromEntries(
                hands.map((h) => [
                  h.teamId,
                  {
                    resources: JSON.stringify(h.resources, null, 2),
                    capabilities: h.capabilities.join('\n'),
                    liabilities: h.liabilities.join('\n'),
                    privateInfo: h.privateInfo,
                    rationale: h.rationale,
                  },
                ]),
              ),
            );
            setSaved({});
            setError('');
            setStatus(
              `Filled ${hands.length} team hands. Review below. Nothing has been saved or revealed.`,
            );
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Invalid response.');
            setStatus('');
          }
        }}
      >
        Fill all team hands
      </button>
      {status && <p role="status">{status}</p>}
      {error && <p role="alert">{error}</p>}
      {teams.map((team) => (
        <fieldset key={team.teamId} disabled={busy}>
          <legend>{team.name}</legend>
          {Object.entries(labels).map(([key, label]) => (
            <label key={key}>
              {label} · {team.name}
              <textarea
                aria-label={`${label} · ${team.name}`}
                rows={3}
                value={fields[team.teamId]![key as keyof HandFields]}
                onChange={(e) => {
                  setFields((current) => ({
                    ...current,
                    [team.teamId]: { ...current[team.teamId]!, [key]: e.target.value },
                  }));
                  setSaved((current) => ({ ...current, [team.teamId]: false }));
                }}
              />
            </label>
          ))}
          <button
            disabled={saved[team.teamId]}
            onClick={() => {
              void (async () => {
                setBusy(true);
                setError('');
                try {
                  if (board.game.phase !== 'REVEAL' || board.game.gameId !== initial.game.gameId)
                    throw new Error('Game phase changed. Close and reopen the editor.');
                  const updated = await onSave(team, fields[team.teamId]!);
                  setTeams((current) =>
                    current.map((t) => (t.teamId === team.teamId ? updated : t)),
                  );
                  setSaved((current) => ({ ...current, [team.teamId]: true }));
                  setStatus(
                    `${team.name}: hand saved privately. Close this editor and choose Reveal all hands under Game Master · next decision when ready.`,
                  );
                } catch (e) {
                  setError(e instanceof Error ? e.message : 'Save failed. Fields retained.');
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            Save hand · {team.name}
          </button>
          {saved[team.teamId] && <p>Saved privately · not revealed</p>}
        </fieldset>
      ))}
    </section>
  );
}
